import React, { Component } from 'react';
import { Button, Card, CardBody, CardTitle, CardText, Row, Col, Form, FormGroup, Label, Input, Alert, Badge, Nav, NavItem, NavLink } from 'reactstrap';
import { Navigate } from 'react-router-dom';

export class CoffeeMenu extends Component {
  static displayName = CoffeeMenu.name;

  constructor(props) {
    super(props);
    this.state = {
      // Catalog data
      productsByCategory: {},
      sizes: [],
      milkOptions: [],
      extras: {},

      // Orders
      orders: [],
      loading: true,

      // Selected options
      selectedProduct: null,
      selectedSize: null,
      selectedMilk: null,
      selectedExtras: [],
      temperature: 'Hot',
      isDecaf: false,
      customerName: '',
      processPayment: false,

      // UI state
      activeCategory: null,
      orderSuccess: false,
      orderError: null,
      redirectToLogin: false,

      // User & payment
      user: null,
      balance: 0,
      currencies: [],
      defaultCurrency: null,
      selectedCurrencyId: '',
    };
  }

  componentDidMount() {
    this.loadUser();
  }

  loadUser() {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        this.setState({ user, customerName: user.username }, () => {
          this.loadCatalog();
          this.loadOrders();
          if (user.role === 0) {
            this.loadBalance();
            this.loadCurrencies();
          }
        });
      } catch (e) {
        console.error('Error parsing user data:', e);
        this.setState({ redirectToLogin: true });
      }
    } else {
      this.setState({ redirectToLogin: true });
    }
  }

  async loadBalance() {
    const { user } = this.state;
    if (!user) return;

    try {
      const response = await fetch(`api/auth/user/${user.userId}/balance`);
      if (response.ok) {
        const data = await response.json();
        this.setState({
          balance: data.balance,
          defaultCurrency: data.defaultCurrency,
          selectedCurrencyId: data.defaultCurrency?.id || ''
        });
      }
    } catch (error) {
      console.error('Error loading balance:', error);
    }
  }

  async loadCurrencies() {
    const { user } = this.state;
    if (!user) return;

    try {
      const response = await fetch('api/currency', {
        headers: { 'X-User-Id': user.userId }
      });
      if (response.ok) {
        const currencies = await response.json();
        this.setState({ currencies });
      }
    } catch (error) {
      console.error('Error loading currencies:', error);
    }
  }

  async loadCatalog() {
    try {
      const response = await fetch('api/product/catalog');
      if (response.ok) {
        const data = await response.json();
        const categories = Object.keys(data.products);

        // Find default size and milk
        const defaultSize = data.sizes.find(s => s.isDefault) || data.sizes[0];
        const defaultMilk = data.milkOptions.find(m => m.isDefault) || data.milkOptions[0];

        this.setState({
          productsByCategory: data.products,
          sizes: data.sizes,
          milkOptions: data.milkOptions,
          extras: data.extras,
          activeCategory: categories[0] || null,
          selectedSize: defaultSize || null,
          selectedMilk: defaultMilk || null,
          loading: false
        });
      } else {
        this.setState({ loading: false, orderError: 'Failed to load menu' });
      }
    } catch (error) {
      console.error('Error loading catalog:', error);
      this.setState({ loading: false, orderError: 'Failed to load menu' });
    }
  }

  async loadOrders() {
    const { user } = this.state;
    try {
      const url = user ? `api/coffeeorder/orders?userId=${user.userId}` : 'api/coffeeorder/orders';
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        this.setState({ orders: data });
      }
    } catch (error) {
      console.error('Error loading orders:', error);
    }
  }

  handleProductSelect = (product) => {
    const { sizes, milkOptions } = this.state;

    // Reset options based on product configuration
    const defaultSize = product.allowSizeChoice
      ? (sizes.find(s => s.isDefault) || sizes[0])
      : null;
    const defaultMilk = product.allowMilkChoice
      ? (milkOptions.find(m => m.isDefault) || milkOptions[0])
      : null;

    this.setState({
      selectedProduct: product,
      selectedSize: defaultSize,
      selectedMilk: defaultMilk,
      selectedExtras: [],
      temperature: product.allowTemperatureChoice ? 'Hot' : 'Hot',
      isDecaf: false,
      orderSuccess: false,
      orderError: null
    });
  }

  handleExtraToggle = (extra) => {
    this.setState(prev => {
      const exists = prev.selectedExtras.find(e => e.id === extra.id);
      if (exists) {
        return { selectedExtras: prev.selectedExtras.filter(e => e.id !== extra.id) };
      } else {
        return { selectedExtras: [...prev.selectedExtras, extra] };
      }
    });
  }

  handleCancelOrder = async (orderId) => {
    const { user } = this.state;

    if (!user) {
      this.setState({ orderError: 'You must be logged in to cancel orders' });
      return;
    }

    if (!window.confirm('Are you sure you want to cancel this order?')) {
      return;
    }

    try {
      const response = await fetch(`api/coffeeorder/orders/${orderId}/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.userId,
          userRole: user.role || 0
        })
      });

      if (response.ok) {
        await this.loadOrders();
        if (user.role === 0) await this.loadBalance();
        this.setState({ orderSuccess: true, orderError: null });
      } else {
        const error = await response.json();
        this.setState({ orderError: error.message || 'Failed to cancel order' });
      }
    } catch (error) {
      console.error('Error cancelling order:', error);
      this.setState({ orderError: 'Failed to cancel order' });
    }
  }

  handleSubmitOrder = async (e) => {
    e.preventDefault();
    const { selectedProduct, selectedSize, selectedMilk, selectedExtras,
            temperature, isDecaf, customerName, processPayment, selectedCurrencyId, user } = this.state;

    if (!selectedProduct) {
      this.setState({ orderError: 'Please select a product' });
      return;
    }

    // Build order request - for now using legacy format with product name
    // In a full implementation, we'd update the backend to accept the new format
    const orderRequest = {
      coffeeType: 0, // Legacy: using 0 for all products for now
      temperature: temperature === 'Cold' ? 1 : 0,
      isDecaf: isDecaf,
      milkPreference: selectedMilk ? this.getMilkPreferenceIndex(selectedMilk.name) : 0,
      customerName: customerName || null,
      processPayment: processPayment,
      currencyId: selectedCurrencyId || null,
      // Extended fields for new catalog
      productId: selectedProduct.id,
      productName: selectedProduct.name,
      sizeId: selectedSize?.id,
      sizeName: selectedSize?.name,
      extraIds: selectedExtras.map(e => e.id),
      totalPrice: this.calculateTotalPrice()
    };

    try {
      const headers = {
        'Content-Type': 'application/json',
      };
      if (user?.userId) {
        headers['X-User-Id'] = user.userId;
      }

      const response = await fetch('api/coffeeorder/orders', {
        method: 'POST',
        headers,
        body: JSON.stringify(orderRequest)
      });

      if (response.ok) {
        this.setState({
          orderSuccess: true,
          orderError: null,
          selectedProduct: null,
          selectedExtras: [],
          processPayment: false
        });
        await this.loadOrders();
        if (processPayment && user?.role === 0) {
          await this.loadBalance();
        }
      } else {
        const errorData = await response.json();
        this.setState({ orderError: errorData.message || 'Failed to create order' });
      }
    } catch (error) {
      console.error('Error creating order:', error);
      this.setState({ orderError: 'Failed to create order' });
    }
  }

  getMilkPreferenceIndex(name) {
    const mapping = {
      'No Milk': 0, 'None': 0,
      'Whole Milk': 1,
      'Skim Milk': 2,
      'Almond Milk': 3,
      'Oat Milk': 4,
      'Soy Milk': 5,
      'Coconut Milk': 6
    };
    return mapping[name] || 0;
  }

  calculateTotalPrice() {
    const { selectedProduct, selectedSize, selectedMilk, selectedExtras, temperature } = this.state;
    if (!selectedProduct) return 0;

    let price = selectedProduct.basePrice;

    // Add size modifier
    if (selectedSize?.priceModifier) {
      price += selectedSize.priceModifier;
    }

    // Add milk modifier
    if (selectedMilk?.priceModifier) {
      price += selectedMilk.priceModifier;
    }

    // Add cold drink premium
    if (temperature === 'Cold' && selectedProduct.allowTemperatureChoice) {
      price += 0.50;
    }

    // Add extras
    selectedExtras.forEach(extra => {
      price += extra.price || 0;
    });

    return price;
  }

  formatPrice(usdPrice) {
    const { selectedCurrencyId, currencies } = this.state;
    if (selectedCurrencyId) {
      const currency = currencies.find(c => c.id === selectedCurrencyId);
      if (currency) {
        const convertedPrice = usdPrice / currency.exchangeRate;
        return `${currency.symbol}${convertedPrice.toFixed(2)}`;
      }
    }
    return `$${usdPrice.toFixed(2)}`;
  }

  getOrderStatusName(status) {
    const statuses = ['Ordered', 'In Progress', 'Done', 'Received', 'Cancelled'];
    return statuses[status] || 'Unknown';
  }

  getOrderStatusBadgeColor(status) {
    const colors = ['secondary', 'warning', 'success', 'info', 'danger'];
    return colors[status] || 'secondary';
  }

  renderCategoryTabs() {
    const { productsByCategory, activeCategory } = this.state;
    const categories = Object.keys(productsByCategory);

    if (categories.length === 0) return null;

    return (
      <Nav tabs className="mb-3">
        {categories.map(category => (
          <NavItem key={category}>
            <NavLink
              className={activeCategory === category ? 'active' : ''}
              onClick={() => this.setState({ activeCategory: category })}
              style={{ cursor: 'pointer' }}
            >
              {category}
            </NavLink>
          </NavItem>
        ))}
      </Nav>
    );
  }

  renderProducts() {
    const { productsByCategory, activeCategory, selectedProduct, selectedCurrencyId, currencies } = this.state;

    if (!activeCategory || !productsByCategory[activeCategory]) {
      return <Alert color="info">No products available. Please check back later!</Alert>;
    }

    const products = productsByCategory[activeCategory];
    const selectedCurrency = selectedCurrencyId ? currencies.find(c => c.id === selectedCurrencyId) : null;

    return (
      <Row>
        {products.map(product => (
          <Col md="6" key={product.id} className="mb-3">
            <Card
              className={`coffee-card h-100 ${selectedProduct?.id === product.id ? 'border-primary' : ''}`}
              style={{ cursor: 'pointer' }}
              onClick={() => this.handleProductSelect(product)}
            >
              <CardBody>
                <div className="d-flex justify-content-between align-items-start">
                  <CardTitle tag="h5" style={{ fontFamily: 'var(--font-display)' }}>
                    {product.name}
                  </CardTitle>
                  <Badge color="primary" className="ms-2">
                    {this.formatPrice(product.basePrice)}
                  </Badge>
                </div>
                <CardText className="text-muted" style={{ fontSize: '0.9rem' }}>
                  {product.description}
                </CardText>
                <div className="mt-2">
                  {product.allowSizeChoice && <Badge color="light" className="me-1 text-dark">Sizes</Badge>}
                  {product.allowTemperatureChoice && <Badge color="light" className="me-1 text-dark">Hot/Iced</Badge>}
                  {product.allowMilkChoice && <Badge color="light" className="me-1 text-dark">Milk Options</Badge>}
                  {product.allowDecafChoice && <Badge color="light" className="text-dark">Decaf Available</Badge>}
                </div>
                {selectedProduct?.id === product.id && (
                  <div className="mt-2">
                    <Badge color="success">Selected</Badge>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        ))}
      </Row>
    );
  }

  renderOrderForm() {
    const { selectedProduct, selectedSize, selectedMilk, selectedExtras,
            temperature, isDecaf, customerName, processPayment,
            sizes, milkOptions, extras,
            user, balance, currencies, selectedCurrencyId } = this.state;

    if (!selectedProduct) {
      return (
        <Card className="text-center p-4" style={{ background: 'var(--cream-dark)' }}>
          <CardBody>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>☕</div>
            <p className="text-muted mb-0">Select a drink from the menu to customize your order</p>
          </CardBody>
        </Card>
      );
    }

    const totalUsd = this.calculateTotalPrice();
    const isCustomer = user && user.role === 0;
    const extraCategories = Object.keys(extras);

    return (
      <Form onSubmit={this.handleSubmitOrder}>
        <Card className="mb-3" style={{ borderLeft: '4px solid var(--moss)' }}>
          <CardBody>
            <h4 style={{ fontFamily: 'var(--font-display)', color: 'var(--moss-dark)' }}>
              {selectedProduct.name}
            </h4>
            <p className="text-muted mb-0">{selectedProduct.description}</p>
          </CardBody>
        </Card>

        {isCustomer && (
          <Card className="mb-3 balance-display">
            <CardBody>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <small style={{ opacity: 0.8 }}>Your Balance</small>
                  <div className="balance-amount" style={{ fontSize: '1.5rem' }}>
                    ${balance.toFixed(2)}
                  </div>
                </div>
                <div className="text-end">
                  <small style={{ opacity: 0.8 }}>Order Total</small>
                  <div className="balance-amount" style={{ fontSize: '1.5rem' }}>
                    {this.formatPrice(totalUsd)}
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
        )}

        {/* Size Selection */}
        {selectedProduct.allowSizeChoice && sizes.length > 0 && (
          <FormGroup>
            <Label className="fw-bold">Size</Label>
            <div className="d-flex flex-wrap gap-2">
              {sizes.map(size => (
                <Button
                  key={size.id}
                  color={selectedSize?.id === size.id ? 'primary' : 'outline-secondary'}
                  onClick={() => this.setState({ selectedSize: size })}
                  type="button"
                  size="sm"
                >
                  {size.name} ({size.abbreviation})
                  {size.priceModifier > 0 && ` +${this.formatPrice(size.priceModifier)}`}
                </Button>
              ))}
            </div>
          </FormGroup>
        )}

        {/* Temperature Selection */}
        {selectedProduct.allowTemperatureChoice && (
          <FormGroup>
            <Label className="fw-bold">Temperature</Label>
            <div className="d-flex gap-2">
              <Button
                color={temperature === 'Hot' ? 'primary' : 'outline-secondary'}
                onClick={() => this.setState({ temperature: 'Hot' })}
                type="button"
                size="sm"
              >
                Hot
              </Button>
              <Button
                color={temperature === 'Cold' ? 'primary' : 'outline-secondary'}
                onClick={() => this.setState({ temperature: 'Cold' })}
                type="button"
                size="sm"
              >
                Iced (+{this.formatPrice(0.50)})
              </Button>
            </div>
          </FormGroup>
        )}

        {/* Milk Selection */}
        {selectedProduct.allowMilkChoice && milkOptions.length > 0 && (
          <FormGroup>
            <Label className="fw-bold">Milk</Label>
            <Input
              type="select"
              value={selectedMilk?.id || ''}
              onChange={(e) => {
                const milk = milkOptions.find(m => m.id === e.target.value);
                this.setState({ selectedMilk: milk });
              }}
            >
              {milkOptions.map(milk => (
                <option key={milk.id} value={milk.id}>
                  {milk.name}
                  {milk.priceModifier > 0 && ` (+$${milk.priceModifier.toFixed(2)})`}
                  {milk.isDairyFree && ' - Dairy Free'}
                </option>
              ))}
            </Input>
          </FormGroup>
        )}

        {/* Decaf Option */}
        {selectedProduct.allowDecafChoice && (
          <FormGroup check className="mb-3">
            <Label check>
              <Input
                type="checkbox"
                checked={isDecaf}
                onChange={(e) => this.setState({ isDecaf: e.target.checked })}
              />
              Make it Decaf
            </Label>
          </FormGroup>
        )}

        {/* Extras */}
        {extraCategories.length > 0 && (
          <FormGroup>
            <Label className="fw-bold">Extras & Add-ons</Label>
            {extraCategories.map(category => (
              <div key={category} className="mb-2">
                <small className="text-muted d-block mb-1">{category}</small>
                <div className="d-flex flex-wrap gap-1">
                  {extras[category].map(extra => {
                    const isSelected = selectedExtras.find(e => e.id === extra.id);
                    return (
                      <Button
                        key={extra.id}
                        color={isSelected ? 'success' : 'outline-secondary'}
                        onClick={() => this.handleExtraToggle(extra)}
                        type="button"
                        size="sm"
                      >
                        {extra.name}
                        {extra.price > 0 && ` +${this.formatPrice(extra.price)}`}
                      </Button>
                    );
                  })}
                </div>
              </div>
            ))}
          </FormGroup>
        )}

        <hr />

        {/* Customer Name */}
        <FormGroup>
          <Label for="customerName">Name for the order</Label>
          <Input
            type="text"
            id="customerName"
            value={customerName}
            onChange={(e) => this.setState({ customerName: e.target.value })}
            placeholder="Your name"
          />
        </FormGroup>

        {/* Currency Selection */}
        {isCustomer && currencies.length > 0 && (
          <FormGroup>
            <Label for="currency">Payment Currency</Label>
            <Input
              type="select"
              id="currency"
              value={selectedCurrencyId}
              onChange={(e) => this.setState({ selectedCurrencyId: e.target.value })}
            >
              <option value="">USD (Default)</option>
              {currencies.map(currency => (
                <option key={currency.id} value={currency.id}>
                  {currency.name} ({currency.symbol})
                </option>
              ))}
            </Input>
          </FormGroup>
        )}

        {/* Payment Option */}
        <FormGroup check className="mb-3">
          <Label check>
            <Input
              type="checkbox"
              checked={processPayment}
              onChange={(e) => this.setState({ processPayment: e.target.checked })}
            />
            Pay now {isCustomer && `(${this.formatPrice(totalUsd)} from balance)`}
          </Label>
        </FormGroup>

        {/* Order Summary */}
        <Card className="mb-3" style={{ background: 'var(--sage-light)' }}>
          <CardBody className="py-2">
            <div className="d-flex justify-content-between align-items-center">
              <span className="fw-bold">Total:</span>
              <span className="fw-bold" style={{ fontSize: '1.25rem', color: 'var(--moss-dark)' }}>
                {this.formatPrice(totalUsd)}
              </span>
            </div>
          </CardBody>
        </Card>

        <Button color="primary" type="submit" size="lg" block>
          Place Order
        </Button>
      </Form>
    );
  }

  renderOrders() {
    const { orders, user } = this.state;

    // Filter orders to show only user's orders
    const userOrders = user ? orders.filter(o => o.userId === user.userId) : orders;

    if (userOrders.length === 0) {
      return <p className="text-muted text-center">No orders yet. Place your first order above!</p>;
    }

    return (
      <div>
        {userOrders.slice(0, 10).map(order => (
          <Card key={order.id} className="mb-2">
            <CardBody className="py-2">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <strong>Order #{order.id}</strong>
                  <span className="text-muted ms-2" style={{ fontSize: '0.85rem' }}>
                    {new Date(order.orderDate).toLocaleDateString()}
                  </span>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <Badge color={this.getOrderStatusBadgeColor(order.status)}>
                    {this.getOrderStatusName(order.status)}
                  </Badge>
                  <span className="fw-bold">${order.totalPrice.toFixed(2)}</span>
                  {order.status === 0 && (
                    <Button
                      size="sm"
                      color="danger"
                      outline
                      onClick={() => this.handleCancelOrder(order.id)}
                    >
                      Cancel
                    </Button>
                  )}
                </div>
              </div>
            </CardBody>
          </Card>
        ))}
      </div>
    );
  }

  render() {
    const { loading, orderSuccess, orderError, redirectToLogin, productsByCategory } = this.state;

    if (redirectToLogin) {
      return <Navigate to="/login" state={{ message: 'Please sign in to order coffee' }} />;
    }

    if (loading) {
      return (
        <div className="text-center py-5">
          <div style={{ fontSize: '3rem' }}>☕</div>
          <p className="text-muted">Loading menu...</p>
        </div>
      );
    }

    const hasProducts = Object.keys(productsByCategory).length > 0;

    return (
      <div className="coffee-menu-page">
        <div className="text-center mb-4">
          <h1 style={{ fontFamily: 'var(--font-display)', color: 'var(--moss-dark)' }}>
            Order Your Coffee
          </h1>
          <p className="lead" style={{ maxWidth: '500px', margin: '0 auto' }}>
            Each cup is crafted with care, just the way you like it
          </p>
        </div>

        {orderSuccess && (
          <Alert color="success" toggle={() => this.setState({ orderSuccess: false })}>
            Your coffee is on its way! Sit back and relax.
          </Alert>
        )}

        {orderError && (
          <Alert color="danger" toggle={() => this.setState({ orderError: null })}>
            {orderError}
          </Alert>
        )}

        {!hasProducts ? (
          <Alert color="info" className="text-center">
            <p className="mb-0">Our menu is being prepared. Please check back soon!</p>
          </Alert>
        ) : (
          <Row>
            <Col lg="7">
              <h3 style={{ fontFamily: 'var(--font-display)', marginBottom: '1rem', color: 'var(--moss-dark)' }}>
                Our Menu
              </h3>
              {this.renderCategoryTabs()}
              {this.renderProducts()}
            </Col>
            <Col lg="5">
              <h3 style={{ fontFamily: 'var(--font-display)', marginBottom: '1rem', color: 'var(--moss-dark)' }}>
                Your Order
              </h3>
              {this.renderOrderForm()}
            </Col>
          </Row>
        )}

        <hr className="my-5" style={{ borderColor: 'var(--latte)', opacity: 0.5 }} />

        <h3 style={{ fontFamily: 'var(--font-display)', marginBottom: '1.5rem', color: 'var(--moss-dark)' }}>
          Recent Orders
        </h3>
        {this.renderOrders()}
      </div>
    );
  }
}
